import { useState } from 'react';
import { quoteApi } from '../services/quoteApi';
import { scheduledSeatService } from '../services/scheduledSeatService';
import { TacticalMission } from '../components/PlanningBoard/types';

export function usePassengerManifestModal() {
  const [paxModalOpen, setPaxModalOpen] = useState(false);
  const [paxModalTitle, setPaxModalTitle] = useState('');
  const [paxModalQuoteId, setPaxModalQuoteId] = useState<string | null>(null);
  const [paxModalScheduledBookingId, setPaxModalScheduledBookingId] = useState<string | null>(null);
  const [paxModalIssuedAt, setPaxModalIssuedAt] = useState<string | null>(null);
  const [paxModalIssuedBy, setPaxModalIssuedBy] = useState<string | null>(null);
  const [paxModalPassengers, setPaxModalPassengers] = useState<
    Array<{
      name: string;
      nationality: string;
      passportNumber: string;
      status: string;
      baggageWeight: string;
      comment: string;
      phone: string;
    }>
  >([]);
  const [paxModalLoading, setPaxModalLoading] = useState(false);
  const [paxModalError, setPaxModalError] = useState<string | null>(null);
  const [paxIssueLoading, setPaxIssueLoading] = useState(false);
  const [paxEmailTo, setPaxEmailTo] = useState('');
  const [paxEmailCc, setPaxEmailCc] = useState('');
  const [paxEmailBcc, setPaxEmailBcc] = useState('');
  const [paxEmailSending, setPaxEmailSending] = useState(false);

  const buildSimpleManifestHtml = () => {
    const rows = paxModalPassengers
      .map(
        (p, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${p.name || ''}</td>
            <td>${p.nationality || ''}</td>
            <td>${p.passportNumber || ''}</td>
            <td>${p.phone || ''}</td>
          </tr>
        `
      )
      .join('');
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Passenger Manifest</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 24px; color: #0f172a; }
      h1 { margin: 0 0 6px; font-size: 24px; }
      .sub { margin: 0 0 16px; color: #475569; font-size: 12px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #cbd5e1; padding: 8px; font-size: 12px; text-align: left; }
      th { background: #f1f5f9; text-transform: uppercase; font-size: 11px; letter-spacing: .06em; }
    </style>
  </head>
  <body>
    <h1>${paxModalTitle || 'Passenger Manifest'}</h1>
    <p class="sub">Generated ${new Date().toLocaleString()}</p>
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Name</th>
          <th>Nationality</th>
          <th>Passport</th>
          <th>Phone</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  </body>
</html>`;
  };

  const loadManifestByQuoteId = async (quoteId: string, title?: string) => {
    setPaxModalTitle(title || 'Passenger List');
    setPaxModalQuoteId(quoteId);
    setPaxModalScheduledBookingId(null);
    setPaxModalIssuedAt(null);
    setPaxModalIssuedBy(null);
    setPaxModalPassengers([]);
    setPaxEmailTo('');
    setPaxEmailCc('');
    setPaxEmailBcc('');
    setPaxModalError(null);
    setPaxModalLoading(true);
    setPaxModalOpen(true);
    try {
      const manifest = await quoteApi.getPassengerManifest(quoteId);
      const list = Array.isArray(manifest?.Passengers)
        ? manifest.Passengers
        : Array.isArray(manifest?.passengers)
        ? manifest.passengers
        : [];
      const passengers = list
        .map((p: any) => {
          const full = p?.name || p?.fullName;
          const composed = [p?.firstName, p?.lastName].filter(Boolean).join(' ').trim();
          const name = (full || composed || p?.passportName || '').trim();
          if (!name) return null;
          return {
            name,
            nationality: (p?.nationality || '').trim(),
            passportNumber: (p?.passportNumber || '').trim(),
            status: (p?.status || '').trim(),
            baggageWeight: (p?.baggageWeight || '').trim(),
            comment: (p?.comment || '').trim(),
            phone: (p?.phone || '').trim(),
          };
        })
        .filter(Boolean) as Array<{
        name: string;
        nationality: string;
        passportNumber: string;
        status: string;
        baggageWeight: string;
        comment: string;
        phone: string;
      }>;
      setPaxModalPassengers(passengers);
      setPaxModalIssuedAt(manifest?.issuedAt || null);
      setPaxModalIssuedBy(manifest?.issuedBy || null);
    } catch {
      setPaxModalError('Failed to load passenger list.');
    } finally {
      setPaxModalLoading(false);
    }
  };

  const openPaxModal = async (mission: TacticalMission) => {
    if (mission.source === 'SCHEDULED') {
      if (
        !mission.scheduledBookingDbId &&
        mission.quoteId &&
        !String(mission.quoteId).startsWith('SPLAN-') &&
        !String(mission.quoteId).startsWith('SINV-')
      ) {
        await loadManifestByQuoteId(mission.quoteId, mission.customerName || 'Passenger List');
        return;
      }
      const list = Array.isArray(mission.manifestPassengers) ? mission.manifestPassengers : [];
      setPaxModalTitle(mission.customerName || 'Scheduled Passenger List');
      setPaxModalQuoteId(null);
      setPaxModalScheduledBookingId(mission.scheduledBookingDbId || null);
      setPaxModalIssuedAt(null);
      setPaxModalIssuedBy(null);
      setPaxModalPassengers(
        list.map((p) => ({
          name: String(p?.name || '').trim(),
          nationality: String(p?.nationality || '').trim(),
          passportNumber: String(p?.passportNumber || '').trim(),
          status: '',
          baggageWeight: '',
          comment: '',
          phone: String(p?.phone || '').trim(),
        }))
      );
      setPaxEmailTo('');
      setPaxEmailCc('');
      setPaxEmailBcc('');
      setPaxModalError(null);
      setPaxModalLoading(false);
      setPaxModalOpen(true);
      return;
    }
    if (!mission.quoteId) {
      setPaxModalTitle(mission.customerName || 'Passenger List');
      setPaxModalQuoteId(null);
      setPaxModalScheduledBookingId(null);
      setPaxModalIssuedAt(null);
      setPaxModalIssuedBy(null);
      setPaxModalPassengers([]);
      setPaxEmailTo('');
      setPaxEmailCc('');
      setPaxEmailBcc('');
      setPaxModalError('No quote linked for this leg.');
      setPaxModalOpen(true);
      return;
    }
    await loadManifestByQuoteId(mission.quoteId, mission.customerName || 'Passenger List');
  };

  const handleIssueManifest = async () => {
    if (!paxModalQuoteId) return;
    setPaxIssueLoading(true);
    setPaxModalError(null);
    try {
      const manifest = await quoteApi.issuePassengerManifest(paxModalQuoteId);
      setPaxModalIssuedAt(manifest?.issuedAt || null);
      setPaxModalIssuedBy(manifest?.issuedBy || null);
    } catch (err: any) {
      setPaxModalError(err?.response?.data?.error || err?.message || 'Failed to issue manifest.');
    } finally {
      setPaxIssueLoading(false);
    }
  };

  const handleDownloadManifestCsv = () => {
    if (!paxModalPassengers.length) return;
    const escape = (value: string) => `"${String(value || '').replace(/"/g, '""')}"`;
    const header = ['Name', 'Nationality', 'Passport', 'Status', 'Baggage', 'Comment', 'Phone'];
    const rows = paxModalPassengers.map(p => [p.name, p.nationality, p.passportNumber, p.status, p.baggageWeight, p.comment, p.phone]);
    const csv = [header, ...rows].map(row => row.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `manifest-${paxModalQuoteId || 'quote'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadManifestHtml = async () => {
    if (!paxModalQuoteId && !paxModalScheduledBookingId && paxModalPassengers.length === 0) return;
    setPaxModalError(null);
    try {
      const blob =
        paxModalQuoteId || paxModalScheduledBookingId
          ? paxModalQuoteId
            ? await quoteApi.downloadPassengerManifestHtml(paxModalQuoteId)
            : await scheduledSeatService.downloadBookingManifestHtml(String(paxModalScheduledBookingId))
          : new Blob([buildSimpleManifestHtml()], { type: 'text/html;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `manifest-${paxModalQuoteId || paxModalScheduledBookingId}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setPaxModalError(err?.response?.data?.error || err?.message || 'Failed to render template manifest.');
    }
  };

  const handlePrintManifest = async () => {
    try {
      let html = '';
      if (paxModalQuoteId) {
        const blob = await quoteApi.downloadPassengerManifestHtml(paxModalQuoteId);
        html = await blob.text();
      } else if (paxModalScheduledBookingId) {
        const blob = await scheduledSeatService.downloadBookingManifestHtml(String(paxModalScheduledBookingId));
        html = await blob.text();
      } else {
        html = buildSimpleManifestHtml();
      }
      const win = window.open('', '_blank');
      if (!win) return;
      win.document.open();
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 300);
    } catch (err: any) {
      setPaxModalError(err?.response?.data?.error || err?.message || 'Failed to print manifest.');
    }
  };

  const handleEmailManifest = () => {
    const send = async () => {
      const payload = {
        to: paxEmailTo.trim() || undefined,
        cc: paxEmailCc.trim() || undefined,
        bcc: paxEmailBcc.trim() || undefined,
      };
      if (paxModalQuoteId) {
        await quoteApi.sendPassengerManifestEmail(paxModalQuoteId, payload);
        return;
      }
      if (paxModalScheduledBookingId) {
        await scheduledSeatService.sendBookingManifestEmail(String(paxModalScheduledBookingId), payload);
        return;
      }
      if (paxModalPassengers.length === 0) return;
      const subject = encodeURIComponent(`Passenger Manifest - ${paxModalTitle || 'Operational Leg'}`);
      const lines = paxModalPassengers
        .map((p, idx) => `${idx + 1}. ${p.name} | ${p.nationality || '-'} | ${p.passportNumber || '-'} | ${p.phone || '-'}`)
        .join('\n');
      const body = encodeURIComponent(
        `${paxModalTitle || 'Passenger Manifest'}\n\nGenerated: ${new Date().toLocaleString()}\nPassengers:\n${lines}`
      );
      const cc = paxEmailCc.trim() ? `&cc=${encodeURIComponent(paxEmailCc.trim())}` : '';
      const bcc = paxEmailBcc.trim() ? `&bcc=${encodeURIComponent(paxEmailBcc.trim())}` : '';
      const to = paxEmailTo.trim();
      window.location.href = `mailto:${encodeURIComponent(to)}?subject=${subject}&body=${body}${cc}${bcc}`;
    };
    setPaxModalError(null);
    setPaxEmailSending(true);
    send()
      .then(() => {
        if (paxModalQuoteId || paxModalScheduledBookingId) {
          window.alert('Manifest email sent using Outbound Hub mail routing.');
        }
      })
      .catch((err: any) => {
        setPaxModalError(err?.response?.data?.error || err?.message || 'Failed to send manifest email.');
      })
      .finally(() => {
        setPaxEmailSending(false);
      });
  };

  const openAggregatedManifest = (
    title: string,
    passengers: Array<{ name: string; nationality?: string; passportNumber?: string; phone?: string }>
  ) => {
    setPaxModalTitle(title || 'Passenger Manifest');
    setPaxModalQuoteId(null);
    setPaxModalScheduledBookingId(null);
    setPaxModalIssuedAt(null);
    setPaxModalIssuedBy(null);
    setPaxEmailTo('');
    setPaxEmailCc('');
    setPaxEmailBcc('');
    setPaxModalPassengers(
      passengers.map((p) => ({
        name: String(p?.name || '').trim(),
        nationality: String(p?.nationality || '').trim(),
        passportNumber: String(p?.passportNumber || '').trim(),
        status: '',
        baggageWeight: '',
        comment: '',
        phone: String(p?.phone || '').trim(),
      }))
    );
    setPaxModalError(null);
    setPaxModalLoading(false);
    setPaxModalOpen(true);
  };

  return {
    paxModalOpen,
    paxModalTitle,
    paxModalQuoteId,
    paxModalScheduledBookingId,
    canDownloadManifestHtml: !!paxModalQuoteId || !!paxModalScheduledBookingId || paxModalPassengers.length > 0,
    canPrintManifest: !!paxModalQuoteId || !!paxModalScheduledBookingId || paxModalPassengers.length > 0,
    canEmailManifest: paxModalPassengers.length > 0,
    paxEmailTo,
    paxEmailCc,
    paxEmailBcc,
    paxEmailSending,
    paxModalIssuedAt,
    paxModalIssuedBy,
    paxModalPassengers,
    paxModalLoading,
    paxModalError,
    paxIssueLoading,
    setPaxModalOpen,
    setPaxEmailTo,
    setPaxEmailCc,
    setPaxEmailBcc,
    loadManifestByQuoteId,
    openPaxModal,
    openAggregatedManifest,
    handleIssueManifest,
    handleDownloadManifestCsv,
    handleDownloadManifestHtml,
    handlePrintManifest,
    handleEmailManifest,
  };
}
